import { GlobalConfig } from '@/services/gloabl.config';
import { Shape, ShapeType } from '@/services/shape';
import { useLayerStore } from '@/store/use-layer-store';
import { useWatchfaceStore } from '@/store/use-watchface-store';
import { Attributes, ResourceKey } from '@/services/shape/attributes';
import { computed, ref } from 'vue';
import { round } from 'lodash-es';
import { useImagesStore } from '@/store/use-images-store';
import { ImageFormatKey } from '@/resources/image-format';
import { useFormatsStore } from '@/store/use-formats-store';
import { canUse } from '@/utils/can-use.utils';
import { getImageArrayAstLen } from '@/utils/ast/utils/image-array.utils';
import { useShapeTransform } from './use-shape-transform';
import { useIFTTTStore } from '@/store/use-ifttt-store';
import { areConditionsMutuallyExclusive } from './use-watchface-ram-ifttt-sprite';

export interface ThemeRamItem {
  themeId: string;
  ram: number;
}
const _themeRamItems = ref<ThemeRamItem[]>([]);
const _imagesRamCache = ref<Record<string, number>>({});
export const recalculateWatchfaceRaming = ref(false);

/**
 * 图组（ImageArray）内存估算策略：
 * - Q61：普通填充/数字填充都按 1 张图片估算（与序列帧“只取一张”一致）
 * - 其他设备：保留原逻辑（whole -> 若超标且设备支持则退化为 single）
 */
const imageArrayRamMethod = ref<'whole' | 'single'>('whole');

export const useWatchfaceRam = () => {
  const watchfaceStore = useWatchfaceStore();
  const layerStore = useLayerStore();
  const imageStore = useImagesStore();
  const formatStore = useFormatsStore();
  const iftttStore = useIFTTTStore();

  /**
   * 计算图片的内存
   * @param imgEl
   * @param deviceType
   * @param format
   * @returns
   */
  const _calculateImageRam = (
    imgSrc: string,
    format: ImageFormatKey,
    scale: number
  ) => {
    if (!imgSrc) return 0;

    const deviceType = watchfaceStore.deviceType;
    const _16bpp = GlobalConfig['using16bppDeviceTypes'].includes(
      deviceType ?? ''
    );
    // 处理是否需要自动判断格式
    if (GlobalConfig.autoChooseImageFormat) {
      const _autoFormat = formatStore.formats[imgSrc];
      format = _autoFormat ?? 'indexed8';
    }

    const cacheKey = imgSrc + format + String(_16bpp) + String(scale);
    if (_imagesRamCache.value[cacheKey]) return _imagesRamCache.value[cacheKey];

    const { width = 0, height = 0 } = imageStore.imagesMap[imgSrc] ?? {};
    let ram = width * scale * (height * scale);
    if (!ram) return 0;

    switch (format) {
      case 'truecolor':
        ram = ram * (_16bpp ? 2 : 4);
        break;
      case 'truecolor_alpha':
        ram = ram * (_16bpp ? 3 : 4);
        break;
      case 'indexed8':
      case 'alpha8':
      default:
        ram += 1024;
        break;
    }
    _imagesRamCache.value[cacheKey] = ram;
    return ram;
  };
  const _getImageSrcs = (
    resources: Attributes['resources'],
    resourceName: ResourceKey
  ) => {
    for (const colorId of Object.keys(resources?.[resourceName] ?? {})) {
      if (resources?.[resourceName]?.[colorId]?.length) {
        return resources?.[resourceName]?.[colorId];
      }
    }
  };
  const _calculateShapeImagesRam = (data: {
    resources: Attributes['resources'];
    resourceKeys: ResourceKey[][];
    imgFormat: ImageFormatKey;
    /**
     * 默认只计算多个图片中第一个图片的内存值
     */
    imagesLength?: number;
    shapeScale: number | undefined;
  }) => {
    const {
      resourceKeys,
      resources,
      imgFormat,
      imagesLength = 1,
      shapeScale = 1
    } = data;
    let ram = 0;

    resourceKeys.forEach((keys) => {
      const _ramMax = Math.max(
        ...keys.map((key) => {
          const srcs = _getImageSrcs(resources, key) ?? [];
          const _srcs = [...srcs].splice(0, imagesLength);
          return _srcs.reduce((pre, cur) => {
            return _calculateImageRam(cur ?? '', imgFormat, shapeScale) + pre;
          }, 0);
        })
      );
      ram += _ramMax;
    });
    return ram;
  };
  const _getShapeRam = (layer: StoreLayerItem) => {
    const { type, attrs } = layer;
    const { resources = {}, format, spriteType = 'sprite' } = attrs;
    const imgFormat = format
      ? format
      : watchfaceStore.description.imageFormat ?? 'indexed8';

    const shape = Shape.getShape(layer.id ?? '');
    if (!shape) return 0;

    const scale = useShapeTransform().canuseScale(shape)
      ? layer.attrs?.scale ?? 1
      : 1;

    if (type == ShapeType.Image) {
      if (attrs.source) {
        // 指针
        // 计算'pointer0', 'pointer1', 'pointer2', 'pointer3'最大值+阴影的大小
        return _calculateShapeImagesRam({
          resources,
          resourceKeys: [
            ['pointer0', 'pointer1', 'pointer2', 'pointer3'],
            ['pointerShadow']
          ],
          imgFormat,
          shapeScale: scale
        });
      } else {
        // 普通图片
        return _calculateShapeImagesRam({
          resources,
          resourceKeys: [['pointer0']],
          imgFormat,
          shapeScale: scale
        });
      }
    } else if (type == ShapeType.ProgressBar) {
      return _calculateShapeImagesRam({
        resources,
        resourceKeys: [['bg'], ['progress'], ['indicatorImage']],
        imgFormat,
        shapeScale: scale
      });
    } else if (type == ShapeType.Sprite) {
      // 仅计算序列帧时内存，视频情况下，不计算内存
      return spriteType == 'video'
        ? 0
        : _calculateShapeImagesRam({
            resources,
            resourceKeys: [['images']],
            imgFormat,
            shapeScale: scale
          });
    } else if (type == ShapeType.PhotoAlbumBg) {
      return _calculateShapeImagesRam({
        resources,
        resourceKeys: [['photoAlbumFg'], ['photoAlbumBg']],
        imgFormat,
        shapeScale: scale
      });
    } else if (type == ShapeType.ImageArray) {
      const shape = Shape.getShape(layer.id, layer);
      if (!shape) return 0;

      const deviceType = watchfaceStore.deviceType;
      //图组 1张图片大小估算 支持的设备Q61
      const isSaveSinglePic = ['Q61'].includes(deviceType);

      if (isSaveSinglePic) {
        // Q61：无论普通填充/数字填充，都按 1 张 images 估算（与序列帧“只取一张”一致）
        const imagesLength = 1;
        if (attrs.fillType == '数字填充') {
          return (
            _calculateShapeImagesRam({
              resources,
              resourceKeys: [['images']],
              imgFormat,
              imagesLength,
              shapeScale: scale
            }) +
            _calculateShapeImagesRam({
              resources,
              resourceKeys: [['unitIcon'], ['unitIcon2']],
              imgFormat,
              shapeScale: scale
            })
          );
        }
        return _calculateShapeImagesRam({
          resources,
          resourceKeys: [['images']],
          imgFormat,
          imagesLength,
          shapeScale: scale
        });
      }

      // 其他设备：保留原逻辑（whole/single 由 imageArrayRamMethod 控制）
      const imageArraySingle = imageArrayRamMethod.value == 'single';

      // 确定图组内存计算图片数量：
      // - 普通填充：single 时按 1 张，否则按 AST 真实张数
      // - 数字填充：始终按 AST 真实张数
      const imagesLength =
        imageArraySingle && attrs.fillType == '普通填充'
          ? 1
          : getImageArrayAstLen(shape, deviceType);

      if (attrs.fillType == '数字填充') {
        // 需要计算图片数字+单位图片内存值之和
        return (
          _calculateShapeImagesRam({
            resources,
            resourceKeys: [['images']],
            imgFormat,
            imagesLength,
            shapeScale: scale
          }) +
          _calculateShapeImagesRam({
            resources,
            resourceKeys: [['unitIcon'], ['unitIcon2']],
            imgFormat,
            shapeScale: scale
          })
        );
      }

      // 普通填充：内存总数 = 第一个图片内存值 * 数量（imagesLength）
      const imgSrcs = _getImageSrcs(resources, 'images');
      const singleImgRam = _calculateImageRam(
        imgSrcs?.[0] ?? '',
        imgFormat,
        scale
      );
      return singleImgRam * imagesLength;
    } else if (type == ShapeType.Widget) {
      // 计算所有的ram
      let ram = 0;
      layer?.children?.forEach((subLayer) => {
        ram += _getShapeRam(subLayer);
      });

      return ram;
    } else if (type == ShapeType.Slot) {
      // 计算最大的一个
      const rams =
        layer?.children?.map((subLayer) => _getShapeRam(subLayer)) ?? [];
      const max = Math.max(...rams, 0);
      return max;
    }

    return 0;
  };

  const _recalculateThemesRam = () => {
    if (!GlobalConfig.validWatchfaceRam) return;
    const deviceType = watchfaceStore.deviceType;
    if (!deviceType) return;
    try {
      recalculateWatchfaceRaming.value = true;

      const ___themeRamItems: typeof _themeRamItems.value = [];
      layerStore.themes.forEach((theme) => {
        let themeRam = 0;
        const iftttSpriteSourceKeys: {
          [key: string]: any;
        } = {};
        theme.children?.forEach((layer) => {
          const curIfttt = iftttStore.ifttts(theme.id, layer.id);
          if (
            layer.type == 'Sprite' &&
            curIfttt &&
            curIfttt[0]?.condition.children.length > 0
          ) {
            const animation = curIfttt[0].animations;
            const condition = curIfttt[0].condition;
            const hasShowAnimation = animation.some(
              (item) => item.type === 'show'
            );
            const hasWhenCondition = condition.children.some(
              (item) => item.type === 'when'
            );
            if (hasShowAnimation && hasWhenCondition) {
              condition.children.forEach((child: any) => {
                if (child.children) {
                  child.children.forEach((grandChild: any) => {
                    const whenSourceKey = grandChild.whenSourceKey;
                    if (whenSourceKey) {
                      if (!iftttSpriteSourceKeys[whenSourceKey]) {
                        iftttSpriteSourceKeys[whenSourceKey] = [];
                      }
                      const exists = iftttSpriteSourceKeys[whenSourceKey].some(
                        (item: any) => item.layer.id === layer.id
                      );
                      if (!exists) {
                        iftttSpriteSourceKeys[whenSourceKey].push({
                          condition,
                          animation,
                          layer
                        });
                      }
                    }
                  });
                }
              });
            } else {
              const _ram = _getShapeRam(layer);
              themeRam += _ram;
            }
          } else {
            // 计算sprite的内存
            const _ram = _getShapeRam(layer);
            themeRam += _ram;
          }
        });
        Object.keys(iftttSpriteSourceKeys).forEach((key) => {
          const sourceKeyData = iftttSpriteSourceKeys[key];
          const conditions = sourceKeyData.map((item: any) => item.condition);
          sourceKeyData.isMutuallyExclusive =
            areConditionsMutuallyExclusive(conditions);

          if (sourceKeyData.isMutuallyExclusive) {
            // 当条件互斥时，计算每个layer的内存，取最大值
            const maxLayerRam = Math.max(
              ...sourceKeyData.map((item: any) => _getShapeRam(item.layer))
            );
            themeRam += maxLayerRam;
          } else {
            // 当条件不互斥时，尝试找出互斥的子集
            const n = sourceKeyData.length;
            const processedIndices = new Set<number>();

            // 遍历所有可能的子集组合
            for (let i = 0; i < n; i++) {
              if (processedIndices.has(i)) continue;

              const mutualGroup = [sourceKeyData[i]];
              processedIndices.add(i);

              for (let j = i + 1; j < n; j++) {
                if (processedIndices.has(j)) continue;

                const tempGroup = [...mutualGroup, sourceKeyData[j]];
                const tempConditions = tempGroup.map((item) => item.condition);

                if (areConditionsMutuallyExclusive(tempConditions)) {
                  mutualGroup.push(sourceKeyData[j]);
                  processedIndices.add(j);
                }
              }

              if (mutualGroup.length > 1) {
                // 对于互斥的子集，取最大内存值
                const maxGroupRam = Math.max(
                  ...mutualGroup.map((item) => _getShapeRam(item.layer))
                );
                themeRam += maxGroupRam;
              } else {
                // 对于单个元素，直接加入内存值
                themeRam += _getShapeRam(mutualGroup[0].layer);
              }
            }
          }
        });
        // 将byte单位转为kb
        const ramKb = round(themeRam / 1024, 0);
        ___themeRamItems.push({ themeId: theme.id, ram: ramKb });
      });

      _themeRamItems.value = ___themeRamItems;
    } finally {
      recalculateWatchfaceRaming.value = false;
    }
  };

  const _getMaxRamTheme = (themeRamItems: ThemeRamItem[]) => {
    let res: (typeof _themeRamItems.value)[0] | undefined;
    themeRamItems.forEach((item) => {
      if ((res?.ram ?? 0) < item.ram) {
        res = { ...item };
      }
    });
    if (res) {
      const ram = res.ram;
      res.ram = ram;
    }

    return res;
  };
  /**
   * 最大内存的表盘theme信息
   */
  const maxRamTheme = computed(() => {
    return _getMaxRamTheme(_themeRamItems.value);
  });
  const maxRamLimit = computed(() => {
    if (!GlobalConfig.validWatchfaceRam) return 0;
    return watchfaceStore.watchfaceDevice?.maxRamLimit ?? 0;
  });

  const _isOverRam = (themeRamItems: ThemeRamItem[]) => {
    if (!GlobalConfig.validWatchfaceRam) return false;
    const maxRamTheme = _getMaxRamTheme(themeRamItems);
    return maxRamLimit.value
      ? (maxRamTheme?.ram ?? 0) > maxRamLimit.value
      : false;
  };

  const isOverRam = computed(() => {
    return _isOverRam(_themeRamItems.value ?? []);
  });

  return {
    maxRamTheme,
    recalculateThemesRam: () => {
      // 先按照全部图片计算内存（除 Q61 强制 single 外）
      imageArrayRamMethod.value = 'whole';
      _recalculateThemesRam();

      // 如果超过内存值，且设备支持“图组按单图估算”，则退化为 single 再算一次
      if (
        _isOverRam(_themeRamItems.value) &&
        imageArrayRamMethod.value == 'whole' &&
        canUse('image-array-single-ram', watchfaceStore.deviceType).valid
      ) {
        console.log('内存超标，按照单图重新计算内存值');
        imageArrayRamMethod.value = 'single';
        _recalculateThemesRam();
      }
    },
    maxRamLimit,
    isOverRam,
    themeRamItems: _themeRamItems,
    clear: () => {
      _themeRamItems.value = [];
      _imagesRamCache.value = {};
      // _shapesRamCache.value = {};
    },
    checkIsOverRam: _isOverRam,
    imageArrayRamMethod
  };
};
